import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BrandLogo } from "@/components/brand-logo";
import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth, ROLE_HOME, type Role } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ThemeToggle } from "@/lib/theme";

type Search = { redirect?: string };

export const Route = createFileRoute("/login")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    redirect: typeof s.redirect === "string" ? s.redirect : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Đăng nhập — Ezyclass" },
      { name: "description", content: "Đăng nhập vào Ezyclass để quản lý trung tâm, lớp học và học viên. Chọn vai trò Quản lý, Giáo viên hoặc Học sinh." },
      { property: "og:title", content: "Đăng nhập — Ezyclass" },
      { property: "og:description", content: "Đăng nhập Ezyclass — phần mềm quản lý trung tâm học tập cho Việt Nam." },
      { property: "og:url", content: "https://ezyclass.com/login" },
    ],
    links: [{ rel: "canonical", href: "https://ezyclass.com/login" }],
  }),
  component: LoginPage,
});

const ROLE_VI: Record<Role, string> = {
  admin: "Quản trị viên",
  teacher: "Giáo viên",
  student: "Học sinh",
};

const ROLE_COLOR: Record<Role, string> = {
  admin: "bg-blue-100 text-blue-700 dark:text-blue-300",
  teacher: "bg-emerald-100 text-emerald-700 dark:text-emerald-300",
  student: "bg-amber-100 text-amber-700 dark:text-amber-300",
};

const ROLE_LABEL: Record<Role, string> = {
  admin: "Admin",
  teacher: "Giáo viên",
  student: "Học sinh",
};

function LoginPage() {
  const { user } = useAuth();
  const { redirect } = Route.useSearch();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [role, setRole] = React.useState<Role>("student");
  const [submitting, setSubmitting] = React.useState(false);
  const [googleLoading, setGoogleLoading] = React.useState(false);
  
  const [rememberMe, setRememberMe] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("ezyclass.remember") === "true";
  });

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem("ezyclass.remember") !== "true") return;
    const savedEmail = window.localStorage.getItem("ezyclass.saved_email");
    const savedRole = window.localStorage.getItem("ezyclass.saved_role") as Role | null;
    if (savedEmail) setEmail(savedEmail);
    if (savedRole === "admin" || savedRole === "teacher" || savedRole === "student") setRole(savedRole);
  }, []);

  const goToPortal = React.useCallback((r: Role) => {
    const safeRedirect = redirect && redirect.startsWith("/") && !redirect.startsWith("/login")
      ? redirect : null;
    const target = safeRedirect ?? ROLE_HOME[r];
    window.location.assign(target);
  }, [redirect]);

  React.useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase.auth.getSession();
      if (!cancelled && data.session) goToPortal(user.role);
    })();
    return () => { cancelled = true; };
  }, [user, goToPortal]);

  const handleEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Email không hợp lệ"); return; }
    setSubmitting(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (error || !data.user) {
        if (error?.message?.toLowerCase().includes("ban")) {
          toast.error("Tài khoản đã bị khóa. Vui lòng liên hệ trung tâm để được hỗ trợ.", { duration: 6000 });
        } else {
          toast.error("Email hoặc mật khẩu không đúng.");
        }
        return;
      }
      let resolvedRole: Role = role;
      try {
        const { ensureUserRole } = await import("@/lib/auth-bootstrap.functions");
        const res = await ensureUserRole({ data: { role } });
        resolvedRole = res.role as Role;
      } catch (err) {
        console.error("ensureUserRole failed", err);
        toast.error("Không cấu hình được vai trò. Vui lòng thử lại.");
        return;
      }

      // Provision pending center (signup chưa có session — đã lưu vào localStorage)
      if (resolvedRole === "admin" && typeof window !== "undefined") {
        const raw = window.localStorage.getItem("ezyclass_pending_center");
        if (raw) {
          try {
            const parsed = JSON.parse(raw) as { name?: string; slug?: string };
            if (parsed?.name && parsed?.slug) {
              const { provisionAdminAccount } = await import("@/lib/center.functions");
              await provisionAdminAccount({ data: { name: parsed.name, slug: parsed.slug } });
            }
          } catch (provErr) {
            console.error("Provision pending center failed", provErr);
          } finally {
            window.localStorage.removeItem("ezyclass_pending_center");
          }
        }
      }

      if (typeof window !== "undefined") {
        if (rememberMe) {
          window.localStorage.setItem("ezyclass.remember", "true");
          window.localStorage.setItem("ezyclass.saved_email", email.trim());
          window.localStorage.setItem("ezyclass.saved_role", resolvedRole);
        } else {
          window.localStorage.removeItem("ezyclass.remember");
          window.localStorage.removeItem("ezyclass.saved_email");
          window.localStorage.removeItem("ezyclass.saved_role");
        }
      }
      toast.success("Đăng nhập thành công");
      goToPortal(resolvedRole);
    } finally {
      setSubmitting(false);
    }
  };

  const emailPlaceholder =
    role === "admin" ? "tentrungtam@admin.ezyclass"
    : role === "teacher" ? "000001@teacher.ezyclass"
    : "000001@student.ezyclass";

  const handleGoogle = async () => {
    if (googleLoading) return;
    setGoogleLoading(true);
    try {
      // Lưu đích muốn đến (nếu có) để callback đưa về đúng chỗ sau OAuth
      if (redirect && redirect.startsWith("/") && !redirect.startsWith("/login")) {
        window.sessionStorage.setItem("ezyclass.oauth_redirect", redirect);
      }
      const { lovable } = await import("@/integrations/lovable");
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin + "/auth/callback",
      });
      if (result.error) {
        toast.error("Không thể bắt đầu đăng nhập Google. Vui lòng thử lại.");
        return;
      }
      if (result.redirected) return; // Trình duyệt đang chuyển tới Google
      // Token đã nhận và session đã đặt — về callback để gán vai trò & điều hướng
      window.location.assign("/auth/callback");
    } catch (err) {
      console.error("[google-signin]", err);
      toast.error("Không thể bắt đầu đăng nhập Google. Vui lòng thử lại.");
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] grid lg:grid-cols-2 bg-white dark:bg-slate-900 relative">
      <div className="absolute top-4 right-4 z-50"><ThemeToggle /></div>
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-slate-950 p-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-blue-600/10 blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-indigo-600/10 blur-[100px]" />

        <Link to="/" className="relative z-10 flex items-center gap-2.5 w-fit">
          <BrandLogo onDark size="size-9" />
          <span className="font-display font-semibold text-lg text-white tracking-tight">Ezyclass</span>
        </Link>

        <div className="relative z-10 max-w-md">
          <blockquote className="font-display text-2xl md:text-3xl text-white/90 leading-snug tracking-tight">
            "Ezyclass giúp mình quản lý 200+ học viên dễ dàng. Tiết kiệm 10 tiếng mỗi tuần."
          </blockquote>
          <div className="mt-6 flex items-center gap-3">
            <div className="size-10 rounded-full bg-gradient-to-br from-blue-500/40 to-indigo-500/40 ring-1 ring-white/10" />
            <div>
              <div className="text-sm font-medium text-white">Nguyễn Văn A</div>
              <div className="text-xs text-white/40">Giám đốc Trung tâm ABC</div>
            </div>
          </div>
        </div>

        <div className="relative z-10 text-xs text-white/30">© 2026 Ezyclass</div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
          className="w-full max-w-[400px]"
        >
          <div className="lg:hidden flex items-center gap-2.5 mb-10">
            <Link to="/" className="flex items-center gap-2.5">
              <BrandLogo size="size-9" />
              <span className="font-display font-semibold text-lg tracking-tight">Ezyclass</span>
            </Link>
          </div>

          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Đăng nhập</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">Nhập email và mật khẩu để tiếp tục.</p>

          <div className="mt-6">
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Loại tài khoản (tùy chọn)</p>
            <div className="flex gap-1.5 p-1 rounded-full bg-slate-100 dark:bg-slate-800 ring-1 ring-slate-200 dark:ring-slate-800/50">
              {(["admin", "teacher", "student"] as Role[]).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={cn(
                    "flex-1 py-2 rounded-full text-[13px] font-medium transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]",
                    role === r
                      ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm"
                      : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:text-slate-200",
                  )}
                >
                  {ROLE_LABEL[r]}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleEmail} className="mt-6 space-y-4">

                <div>
                  <label htmlFor="email" className="text-sm font-medium text-slate-700 dark:text-slate-200">Email</label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={emailPlaceholder}
                    className="mt-1.5 h-11 rounded-xl bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 focus-visible:bg-white dark:bg-slate-900 focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:border-blue-500 transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="password" className="text-sm font-medium text-slate-700 dark:text-slate-200">Mật khẩu</label>
                  <div className="relative mt-1.5">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="h-11 rounded-xl bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 pr-10 focus-visible:bg-white dark:bg-slate-900 focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:border-blue-500 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                      aria-pressed={showPassword}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:text-slate-400 transition"
                    >
                      {showPassword ? <EyeOff className="size-4" strokeWidth={1.5} /> : <Eye className="size-4" strokeWidth={1.5} />}
                    </button>

                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    id="rememberMe"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="size-4 rounded border-slate-300 dark:border-slate-700 accent-blue-600 cursor-pointer"
                  />
                  <label htmlFor="rememberMe" className="text-sm text-slate-500 dark:text-slate-400 cursor-pointer select-none">
                    Nhớ đăng nhập
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 font-medium active:scale-[0.99] transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]"
                >
                  {submitting ? <Loader2 className="size-4 animate-spin" /> : "Đăng nhập"}
                </Button>
              </form>

              <div className="mt-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
                <span className="text-xs text-slate-400 dark:text-slate-500">hoặc</span>
                <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
              </div>

              <button
                type="button"
                onClick={handleGoogle}
                disabled={googleLoading}
                className="mt-4 w-full h-11 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 hover:bg-slate-50 dark:hover:bg-slate-900 font-medium text-slate-700 dark:text-slate-200 flex items-center justify-center gap-2.5 active:scale-[0.99] transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] disabled:opacity-60"
              >
                {googleLoading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <>
                    <svg className="size-4" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/>
                      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52Z"/>
                    </svg>
                    Đăng nhập với Google
                  </>
                )}
              </button>

              <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-6">
                Chưa có tài khoản?{" "}
                <Link to="/signup" className="text-blue-600 hover:text-blue-700 dark:text-blue-300 font-medium">
                  Đăng ký trung tâm mới
                </Link>
              </p>

          <div className="mt-8 pt-6 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-400 dark:text-slate-500">
            <Link to="/" className="hover:text-slate-600 dark:text-slate-400 transition">← Về trang chủ</Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
